import { useEffect, useMemo } from "react";
import { format } from "date-fns";
import { CalendarIcon } from "lucide-react";

import { useTran } from "@/hooks";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Calendar } from "@/components/ui/calendar";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { cn } from "@/lib/utils";

const parseIsoDateToLocalDate = (value) => {
  if (!value) return undefined;
  const raw = String(value).slice(0, 10);
  const [year, month, day] = raw.split("-").map(Number);
  if (!year || !month || !day) return undefined;
  return new Date(year, month - 1, day);
};

const toUtcIsoDate = (date) => {
  if (!date) return null;
  return new Date(
    Date.UTC(date.getFullYear(), date.getMonth(), date.getDate()),
  ).toISOString();
};

export default function DatePickerField({
  label,
  value,
  onChange,
  placeholder,
  required = false,
  disabled = false,
  error,
  className,
  customFormat = "M/d/yyyy",
  year,
  reportingYear = null,
  isEdit = false,
}) {
  const { t } = useTran();
  const selectedDate = useMemo(() => parseIsoDateToLocalDate(value), [value]);
  const resolvedPlaceholder = placeholder ?? t("Pick a date");

  const minDate = useMemo(() => {
    if (reportingYear?.startYear && reportingYear?.startMonth) {
      return new Date(reportingYear.startYear, reportingYear.startMonth - 1, 1);
    }

    return year ? new Date(year, 0, 1) : undefined;
  }, [reportingYear, year]);

  const maxDate = useMemo(() => {
    if (reportingYear?.endYear && reportingYear?.endMonth) {
      return new Date(
        reportingYear.endYear,
        reportingYear.endMonth,
        0, // last day of end month
      );
    }

    return year ? new Date(year, 11, 31) : undefined;
  }, [reportingYear, year]);

  const calendarMonth =
    selectedDate || minDate || (year ? new Date(year, 0, 1) : undefined);

  useEffect(() => {
    if (!reportingYear || isEdit) return;

    const resetDate = new Date(
      reportingYear.startYear,
      reportingYear.startMonth - 1,
      1,
    );

    onChange?.(toUtcIsoDate(resetDate));
  }, [reportingYear?.startYear, reportingYear?.startMonth, isEdit]);

  return (
    <div className={cn("space-y-2", className)}>
      {label ? (
        <Label>
          {label}
          {required && <span className="ml-1 text-destructive">*</span>}
        </Label>
      ) : null}

      <Popover>
        <PopoverTrigger asChild>
          <Button
            type="button"
            variant="outline"
            disabled={disabled}
            className={cn(
              "w-full min-w-0 justify-start overflow-hidden text-left font-normal",
              !selectedDate && "text-muted-foreground",
              error && "border-destructive dark:border-destructive",
            )}
          >
            <CalendarIcon className="h-4 w-4 shrink-0" />
            <span className="truncate">
              {selectedDate
                ? format(selectedDate, customFormat)
                : resolvedPlaceholder}
            </span>
          </Button>
        </PopoverTrigger>
        <PopoverContent
          className="w-auto max-w-[calc(100vw-1rem)] p-0"
          align="start"
        >
          <Calendar
            mode="single"
            selected={selectedDate}
            onSelect={(date) => onChange?.(toUtcIsoDate(date))}
            autoFocus
            defaultMonth={calendarMonth}
            startMonth={minDate}
            endMonth={maxDate}
            disabled={[{ before: minDate }, { after: maxDate }].filter(
              (range) => range.before || range.after,
            )}
          />
        </PopoverContent>
      </Popover>

      {error ? <p className="text-xs text-destructive">{error}</p> : null}
    </div>
  );
}
